import React, { useState, useEffect, useCallback } from 'react';
import { adminBusinessPartnerAPI } from '../../../services/api';

// ── Default permission template ───────────────────────────────────────────────
const DEFAULT_PERMISSIONS = {
  dashboard:    { read: true,  write: false },
  merchants:    { read: true,  write: false },
  kyc_links:    { read: true,  write: true  },
  transactions: { read: true,  write: false },
  settlements:  { read: false, write: false },
};

const PERMISSION_LABELS = {
  dashboard:    'Dashboard',
  merchants:    'Merchants',
  kyc_links:    'KYC Links',
  transactions: 'Transactions',
  settlements:  'Settlements',
};

// ── Status badge ──────────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const map = {
    active:   { bg: '#D1FAE5', color: '#059669', label: 'Active' },
    inactive: { bg: '#FEE2E2', color: '#DC2626', label: 'Inactive' },
  };
  const s = map[status] || map.inactive;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700, padding: '2px 10px',
      borderRadius: 20, letterSpacing: 0.3,
    }}>{s.label}</span>
  );
};

// ── Permission toggle grid ────────────────────────────────────────────────────
const PermissionGrid = ({ permissions, onChange }) => (
  <div style={{ border: '1px solid #e8e8f1', borderRadius: 8, overflow: 'hidden' }}>
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 80px 80px',
      background: '#f9f9fc', padding: '8px 14px',
      fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#4a4a6a',
    }}>
      <span>Feature</span><span style={{ textAlign: 'center' }}>Read</span><span style={{ textAlign: 'center' }}>Write</span>
    </div>
    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
      <div key={key} style={{
        display: 'grid', gridTemplateColumns: '1fr 80px 80px',
        padding: '10px 14px', borderTop: '1px solid #f0f0f6', alignItems: 'center',
      }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#14142b' }}>{label}</span>
        {['read', 'write'].map(type => (
          <div key={type} style={{ textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={!!(permissions[key]?.[type])}
              onChange={e => {
                const next = {
                  ...permissions,
                  [key]: { ...(permissions[key] || {}), [type]: e.target.checked },
                };
                // write requires read
                if (type === 'write' && e.target.checked) next[key].read = true;
                if (type === 'read' && !e.target.checked) next[key].write = false;
                onChange(next);
              }}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#14142b' }}
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// ── Create / Edit modal ───────────────────────────────────────────────────────
const Modal = ({ partner, onClose, onSaved }) => {
  const isEdit = !!partner?.id;
  const [form, setForm] = useState({
    full_name:           partner?.full_name           || '',
    email:               partner?.email               || '',
    username:            partner?.username             || '',
    password:            '',
    status:              partner?.status              || 'active',
    partner_permissions: partner?.partner_permissions || { ...DEFAULT_PERMISSIONS },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name || !form.email) { setError('Name and Email required.'); return; }
    if (!isEdit && !form.password) { setError('Password required.'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) {
        await adminBusinessPartnerAPI.update(partner.id, {
          full_name:           form.full_name,
          status:              form.status,
          partner_permissions: form.partner_permissions,
          ...(form.password ? { password: form.password } : {}),
        });
      } else {
        await adminBusinessPartnerAPI.create(form);
      }
      onSaved();
    } catch (err) {
      const errs = err.response?.data?.errors;
      setError(errs ? Object.values(errs).flat().join(' ') : (err.response?.data?.message || 'Error occurred.'));
    } finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1.5px solid #e8e8f1',
    borderRadius: 8, fontSize: 13, color: '#14142b', outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '28px 24px',
        width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: '#14142b' }}>
          {isEdit ? 'Edit Business Partner' : 'Create Business Partner'}
        </h3>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 5 }}>Full Name *</label>
            <input style={inputStyle} value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Partner full name" />
          </div>

          {!isEdit && (
            <>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 5 }}>Email *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="partner@email.com" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 5 }}>Username *</label>
                <input style={inputStyle} value={form.username} onChange={e => set('username', e.target.value)} placeholder="partner_username" />
              </div>
            </>
          )}

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 5 }}>
              {isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}
            </label>
            <input style={inputStyle} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
          </div>

          {isEdit && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 5 }}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>Permissions</label>
            <PermissionGrid
              permissions={form.partner_permissions}
              onChange={p => set('partner_permissions', p)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{ padding: '9px 20px', border: '1.5px solid #e8e8f1', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4a4a6a' }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={loading}
            style={{ padding: '9px 22px', border: 'none', borderRadius: 8, background: '#14142b', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#fff', opacity: loading ? 0.7 : 1 }}
          >{loading ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Partner')}</button>
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminBusinessPartners = () => {
  const [partners, setPartners] = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null);  // null | {} | {id,...}
  const [toast, setToast]       = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true); setError('');
    try {
      const res = await adminBusinessPartnerAPI.list({ search, page });
      setPartners(res.data.data);
      setMeta(res.data.meta || {});
    } catch {
      setError('Failed to load business partners.');
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this business partner?')) return;
    try {
      await adminBusinessPartnerAPI.deactivate(id);
      showToast('Partner deactivated.');
      load();
    } catch { showToast('Error deactivating partner.'); }
  };

  const handleRegenCode = async (id) => {
    try {
      const res = await adminBusinessPartnerAPI.regenerateCode(id);
      showToast('New referral code: ' + res.data.referral_code);
      load();
    } catch { showToast('Error regenerating code.'); }
  };

  const onSaved = () => { setModal(null); showToast('Saved successfully!'); load(); };

  return (
    <>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: '#F0FDF4', border: '1.5px solid #86EFAC', color: '#15803D',
          padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,.1)',
        }}>{toast}</div>
      )}

      {/* Modal */}
      {modal !== null && (
        <Modal
          partner={modal}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <style>{`
          .bp-kpi-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 20px; }
          @media (max-width: 700px) { .bp-kpi-grid { grid-template-columns: 1fr 1fr; } }
        `}</style>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#14142b', letterSpacing: '-.02em' }}>Business Partners</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6e6e86' }}>Manage reseller and referral partners</p>
          </div>
          <button onClick={() => setModal({})}
            style={{ height: 38, padding: '0 18px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            + New Business Partner
          </button>
        </div>

        {/* KPI Cards */}
        <div className="bp-kpi-grid">
          {[
            { label: 'Total Partners',   value: meta?.total ?? partners.length, color: '#7c3aed', bg: '#f1ebff' },
            { label: 'Active Partners',  value: partners.filter(p => p.status === 'active').length, color: '#10B981', bg: '#D1FAE5' },
            { label: 'Total Merchants',  value: partners.reduce((s, p) => s + (p.merchant_count || 0), 0), color: '#06B6D4', bg: '#E0F9FF' },
          ].map(k => (
            <div key={k.label} style={{ background: '#fff', border: `1.5px solid #e8e8f1`, borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: k.color, lineHeight: 1, minHeight: 30, display: 'flex', alignItems: 'center' }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: .4 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#14142b" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or username…"
            style={{ width: '100%', height: 40, padding: '0 14px 0 36px', border: '1.5px solid #e8e8f1', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86' }}>Loading…</div>
        ) : (
          <div style={{ border: '1px solid #e8e8f1', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1.2fr 100px 120px 130px',
              background: '#f9f9fc', padding: '10px 16px',
              fontSize: 10, fontWeight: 700, color: '#4a4a6a', letterSpacing: 1, textTransform: 'uppercase',
            }}>
              <span>Partner</span><span>Email</span><span>Merchants</span><span>Referral Code</span><span>Actions</span>
            </div>

            {partners.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: '#6e6e86', fontSize: 14 }}>
                No business partners yet. Create one to get started.
              </div>
            )}

            {partners.map((p, i) => (
              <div key={p.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 1.2fr 100px 120px 130px',
                padding: '14px 16px', alignItems: 'center',
                borderTop: i > 0 ? '1px solid #f0f0f6' : 'none',
                background: i % 2 === 0 ? '#fff' : '#FAFAFA',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#14142b' }}>{p.full_name}</div>
                  <div style={{ fontSize: 11, color: '#6e6e86' }}>@{p.username}</div>
                  <div style={{ marginTop: 3 }}><Badge status={p.status} /></div>
                </div>
                <div style={{ fontSize: 12, color: '#4a4a6a', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.email}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#14142b', textAlign: 'center' }}>{p.merchant_count}</div>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#14142b', background: '#F1F5F9', padding: '3px 8px', borderRadius: 6, display: 'inline-block' }}>
                    {p.referral_code}
                  </div>
                  <button
                    onClick={() => handleRegenCode(p.id)}
                    style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', color: '#4a4a6a', fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}
                  >Regenerate</button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setModal(p)}
                    style={{ padding: '5px 12px', border: '1px solid #e8e8f1', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#14142b' }}
                  >Edit</button>
                  {p.status === 'active' && (
                    <button
                      onClick={() => handleDeactivate(p.id)}
                      style={{ padding: '5px 12px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#DC2626' }}
                    >Deactivate</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            {Array.from({ length: meta.last_page }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => load(page)}
                style={{
                  width: 36, height: 36, borderRadius: 8, border: '1px solid #e8e8f1',
                  background: page === meta.current_page ? '#14142b' : '#fff',
                  color: page === meta.current_page ? '#fff' : '#14142b',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >{page}</button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminBusinessPartners;
